// v0.5.0g — Excluded-transaction backlog admin page.
//
// Closes KL-1: every transaction that's been Supersede'd or
// Route'd out of a filing shows up here as `open` until an
// operator explicitly resolves it. Two resolution paths:
//   - "Filed later"   — the txn will be reported in a future
//                       calc (operator names the target calc id).
//   - "Not a sale"    — bad source data; no future filing
//                       needed (operator records why).
//
// While any entry is `open` for a given calc, the XLSX export
// endpoint refuses to ship that calc with a 409 +
// `EXCLUDED_TXNS_UNRESOLVED`. The pre-export gate is in
// `routes_export.py` (v0.5.0g hook).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { API } from "../lib/api";
import { getToken } from "../lib/authApi";
import { writeFetch } from "../lib/sharedRead";   // A1-95
import { BulkResolveByCalc } from "../components/BulkResolveByCalc";
import { FiledCalcSelect, laterFilingFilter } from "../components/FiledCalcSelect";
import { PresetReasonSelect } from "../components/PresetReasonSelect";
import { BACKLOG_REASONS } from "../lib/reviewReasonPresets";


const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const m = {
    open: { cls: "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-700", label: t("ui.excluded_backlog.status_open") },
    resolved_filed_later: { cls: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700", label: t("ui.excluded_backlog.status_filed_later") },
    resolved_not_a_sale: { cls: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700", label: t("ui.excluded_backlog.status_not_a_sale") },
    // v0.6.12 A2-182 — sky, not emerald: the row IS a real sale and is
    // filed somewhere, just not here. Distinct from "not a real sale"
    // (slate, nothing to file) and from "filed later" (emerald, filed
    // by us).
    resolved_another_client: { cls: "bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 border border-sky-300 dark:border-sky-700", label: t("ui.excluded_backlog.status_another_client") },
    // A1-103 step 4 — an exact copy left out on the review card; its first
    // row is on the return. Muted: nothing to do.
    resolved_duplicate: { cls: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700", label: t("ui.excluded_backlog.status_duplicate") },
  }[status] || { cls: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700", label: status };
  return (
    <span
      data-testid={`xtxn-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.cls}`}
    >
      {m.label}
    </span>
  );
};


// A2-619 — a copy left out as a duplicate is counted again from the next
// re-run (the review card then asks about it again).
const PutBack = ({ entry, onDone }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (entry.put_back_at) {
    return (
      <span data-testid={`xtxn-put-back-done-${entry.id}`} className="text-[11px] text-slate-600 dark:text-slate-300">
        {t("ui.excluded_backlog.put_back_done", { defaultValue: "Put back — counted again from the next re-run" })}
      </span>
    );
  }
  const go = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await writeFetch(`${API}/admin/excluded-txns/${entry.id}/put-back`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail?.message || j.detail || `HTTP ${res.status}`);
      }
      onDone();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <button type="button" data-testid={`xtxn-put-back-${entry.id}`} onClick={go} disabled={busy} aria-busy={busy}
              className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-400 dark:border-slate-500 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60">
        {busy ? t("ui.excluded_backlog.put_back_busy", { defaultValue: "Saving…" })
          : t("ui.excluded_backlog.put_back", { defaultValue: "Put back" })}
      </button>
      {err && <span role="alert" className="text-[11px] text-rose-700 dark:text-rose-300">{err}</span>}
    </span>
  );
};

const ResolveForm = ({ entry, onResolved, onCancel }) => {
  const { t } = useTranslation();
  const [resolution, setResolution] = useState("not_a_sale");
  const [note, setNote] = useState("");
  const [preset, setPreset] = useState("");
  const [filedInCalc, setFiledInCalc] = useState("");

  // v0.6.12 A5-27 — the per-entry form now picks from the same
  // BACKLOG_REASONS the bulk form already used. Each resolution has its
  // own list, so switching clears a reason picked under another one.
  const pickResolution = (next) => {
    if (next === resolution) return;
    setResolution(next); setPreset(""); setNote("");
  };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const valid = note.trim().length >= 10
    && !/[<>]/.test(note)
    && (resolution !== "filed_later" || filedInCalc.trim().length > 0);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await writeFetch(`${API}/admin/excluded-txns/${entry.id}/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          resolution,
          resolution_note: note.trim(),
          filed_in_calc_id: resolution === "filed_later" ? filedInCalc.trim() : null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      onResolved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`xtxn-resolve-form-${entry.id}`}
      className="mt-2 p-3 rounded-md bg-slate-50 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 space-y-2"
    >
      <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {t("ui.excluded_backlog.resolve_question")}
      </div>
      <div className="flex gap-2 text-xs">
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="radio" name={`res-${entry.id}`}
            data-testid={`xtxn-radio-not-a-sale-${entry.id}`}
            checked={resolution === "not_a_sale"}
            onChange={() => pickResolution("not_a_sale")}
          />
          {t("ui.excluded_backlog.radio_not_a_sale")}
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="radio" name={`res-${entry.id}`}
            data-testid={`xtxn-radio-filed-later-${entry.id}`}
            checked={resolution === "filed_later"}
            onChange={() => pickResolution("filed_later")}
          />
          {t("ui.excluded_backlog.radio_filed_later")}
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="radio" name={`res-${entry.id}`}
            data-testid={`xtxn-radio-another-client-${entry.id}`}
            checked={resolution === "another_client"}
            onChange={() => pickResolution("another_client")}
          />
          {t("ui.excluded_backlog.radio_another_client")}
        </label>
      </div>
      {resolution === "filed_later" && (
        <FiledCalcSelect
          testid={`xtxn-filed-calc-${entry.id}`}
          value={filedInCalc}
          onChange={(id) => setFiledInCalc(id)}
          filter={laterFilingFilter(entry.excluded_in_calc_id)}
          aria-label={t("ui.excluded_backlog.filed_calc_pick", { defaultValue: "The calculation where this transaction was reported" })}
        />
      )}
      <PresetReasonSelect
        testid={`xtxn-note-preset-${entry.id}`}
        presets={BACKLOG_REASONS[resolution] || []}
        preset={preset}
        onPick={(p, v) => { setPreset(p); setNote(v); }}
      />
      {err && (
        <div data-testid={`xtxn-resolve-error-${entry.id}`} className="text-[11px] text-rose-700 dark:text-rose-300">{err}</div>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          data-testid={`xtxn-resolve-cancel-${entry.id}`}
          onClick={onCancel}
          className="text-[11px] underline text-slate-600 dark:text-slate-300"
        >
          {t("ui.excluded_backlog.btn_cancel")}
        </button>
        <button
          type="button"
          data-testid={`xtxn-resolve-submit-${entry.id}`}
          data-resolution-class="recordkeeping"
          data-resolution-endpoint="POST /api/admin/excluded-txns/{entry_id}/resolve"
          disabled={busy || !valid}
          onClick={submit}
          className="text-xs font-bold px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
        >
          {busy ? t("ui.excluded_backlog.submitting") : t("ui.excluded_backlog.submit_confirm")}
        </button>
      </div>
    </div>
  );
};


export default function ExcludedBacklog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API}/admin/excluded-txns?status=${statusFilter}`, {
        headers: { "Authorization": `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fmtMoney = (s) => {
    const n = Number(s);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : s;
  };

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="excluded-backlog-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              type="button"
              data-testid="excluded-backlog-back"
              onClick={() => navigate("/")}
              className="text-xs underline text-slate-600 dark:text-slate-300 mb-2 block"
            >
              {t("ui.excluded_backlog.back")}
            </button>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {t("ui.excluded_backlog.title")}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 max-w-2xl mt-1">
              {t("ui.excluded_backlog.body")}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {[
              ["open", t("ui.excluded_backlog.filter_open")],
              ["resolved_filed_later", t("ui.excluded_backlog.filter_filed_later")],
              ["resolved_not_a_sale", t("ui.excluded_backlog.filter_not_a_sale")],
              ["resolved_another_client", t("ui.excluded_backlog.filter_another_client")],
              ["resolved_duplicate", t("ui.excluded_backlog.filter_duplicate")],
              ["all", t("ui.excluded_backlog.filter_all")],
            ].map(([s, label]) => (
              <button
                key={s}
                type="button"
                data-testid={`xtxn-filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded border ${
                  statusFilter === s
                    ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-900 dark:border-slate-100"
                    : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* v0.6.10 — per-calc bulk resolution. Only in the "open"
            view: resolving a resolved entry is meaningless. One panel
            per calc, because the endpoint is scoped to one calc and
            the operator should never be offered a control that spans
            filings. */}
        {!loading && statusFilter === "open" && data?.entries?.length > 0 && (
          <div
            data-testid="xtxn-bulk-panels"
            className="mb-4 flex flex-col gap-2"
          >
            {Object.entries(
              (data.entries || []).reduce((acc, e) => {
                const k = e.excluded_in_calc_id;
                acc[k] = (acc[k] || 0) + 1;
                return acc;
              }, {}),
            ).map(([cid, n]) => (
              <BulkResolveByCalc
                key={cid}
                calcId={cid}
                openCount={n}
                onDone={load}
              />
            ))}
          </div>
        )}

        {loading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">{t("ui.excluded_backlog.loading")}</div>
        )}
        {err && (
          <div data-testid="excluded-backlog-error" className="text-sm text-rose-700 dark:text-rose-300 p-3 rounded border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/40">
            {err}
          </div>
        )}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">{t("ui.excluded_backlog.stat_entries")}</div>
                <div data-testid="xtxn-summary-count" className="text-2xl font-bold text-slate-900 dark:text-slate-100">{data.summary?.count ?? 0}</div>
              </div>
              <div className="p-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">{t("ui.excluded_backlog.stat_unresolved_net")}</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">{fmtMoney(data.summary?.total_net)}</div>
              </div>
              <div className="p-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">{t("ui.excluded_backlog.stat_unresolved_vat")}</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">{fmtMoney(data.summary?.total_vat)}</div>
              </div>
              <div className="p-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">{t("ui.excluded_backlog.stat_by_country")}</div>
                <div data-testid="xtxn-summary-countries" className="text-xs tabular-nums text-slate-700 dark:text-slate-200 mt-2">
                  {Object.entries(data.summary?.by_country || {}).map(([cc, n]) => (
                    <span key={cc} className="mr-2">{cc}:{n}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 text-left">
                  <tr>
                    <th className="px-3 py-2">{t("ui.excluded_backlog.col_status")}</th>
                    <th className="px-3 py-2">{t("ui.excluded_backlog.col_country")}</th>
                    <th className="px-3 py-2">{t("ui.excluded_backlog.col_order")}</th>
                    <th className="px-3 py-2">{t("ui.excluded_backlog.col_date")}</th>
                    <th className="px-3 py-2 text-right">{t("ui.excluded_backlog.col_net")}</th>
                    <th className="px-3 py-2 text-right">{t("ui.excluded_backlog.col_vat")}</th>
                    <th className="px-3 py-2">{t("ui.excluded_backlog.col_rate")}</th>
                    <th className="px-3 py-2">{t("ui.excluded_backlog.col_dropped_from")}</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.entries || []).map((e) => (
                    <React.Fragment key={e.id}>
                      <tr
                        data-testid={`xtxn-row-${e.id}`}
                        className="border-t border-slate-200 dark:border-slate-700"
                      >
                        <td className="px-3 py-2"><StatusBadge status={e.status} /></td>
                        <td className="px-3 py-2 tabular-nums">{e.country}</td>
                        <td className="px-3 py-2 tabular-nums">{e.order_id}</td>
                        <td className="px-3 py-2 tabular-nums">{e.transaction_date}</td>
                        <td className="px-3 py-2 tabular-nums text-right">{fmtMoney(e.net_amount)} {e.currency}</td>
                        <td className="px-3 py-2 tabular-nums text-right">{fmtMoney(e.vat_amount)}</td>
                        <td className="px-3 py-2 tabular-nums">{(Number(e.rate_declared) * 100).toFixed(2)}%</td>
                        <td className="px-3 py-2 tabular-nums text-[10px] text-slate-500 dark:text-slate-400">{e.excluded_in_calc_id?.slice(0, 8)}…</td>
                        <td className="px-3 py-2 text-right">
                          {e.status === "resolved_duplicate" && <PutBack entry={e} onDone={load} />}
                          {e.status === "open" && (
                            <button
                              type="button"
                              data-testid={`xtxn-resolve-open-${e.id}`}
                              onClick={() => setExpanded((s) => ({ ...s, [e.id]: !s[e.id] }))}
                              className="text-[11px] font-semibold px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {expanded[e.id] ? t("ui.excluded_backlog.btn_cancel") : t("ui.excluded_backlog.btn_record_resolution")}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded[e.id] && e.status === "open" && (
                        <tr>
                          <td colSpan={9} className="px-3 pb-3 bg-slate-50 dark:bg-slate-900/40">
                            <div className="text-[10px] text-slate-500 dark:text-slate-400 mb-2">
                              <span className="font-semibold">{t("ui.excluded_backlog.reason_label")}</span> {e.reason}
                            </div>
                            <ResolveForm
                              entry={e}
                              onResolved={() => {
                                setExpanded((s) => ({ ...s, [e.id]: false }));
                                load();
                              }}
                              onCancel={() => setExpanded((s) => ({ ...s, [e.id]: false }))}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {(data.entries || []).length === 0 && (
                <div data-testid="xtxn-empty-state" className="px-3 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                  {statusFilter === "open"
                    ? t("ui.excluded_backlog.empty_unresolved")
                    : t("ui.excluded_backlog.empty_filtered")}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </>
  );
}
